// league.js — league tables and race-chart series, computed in the browser
// from the API's compact match list (?matches=1: a team list plus
// [date, homeIdx, awayIdx, homeGoals, awayGoals] rows, oldest first).
// Seasons are start years (2003 = 2003/04).

import { seasonStartYear } from "../heatmap/utils";
import { POINT_DEDUCTIONS } from "./data";

export const MATCHES_QUERY = "matches=1";

// { teams: [{ team, image, lastSeason }], matches: [{ date, season, h, a, hs, as }], seasons: [1992, …] }
export function parseMatches(resp) {
  const teams = resp.teams.map((t) => ({ ...t, lastSeason: 0 }));
  const matches = resp.matches.map(([date, h, a, hs, as]) => {
    const season = seasonStartYear(`${date}T12:00:00Z`);
    teams[h].lastSeason = Math.max(teams[h].lastSeason, season);
    teams[a].lastSeason = Math.max(teams[a].lastSeason, season);
    return { date, season, h, a, hs, as };
  });
  const seasons = [...new Set(matches.map((m) => m.season))].sort((x, y) => x - y);
  return { teams, matches, seasons, currentSeason: seasons[seasons.length - 1] };
}

const deductionsIn = (team, from, to) =>
  POINT_DEDUCTIONS.filter((d) => d.team === team && d.season >= from && d.season <= to).reduce((n, d) => n + d.points, 0);

const rankOrder = (a, b) => b.pts - a.pts || b.gd - a.gd || b.goalsFor - a.goalsFor || a.team.localeCompare(b.team);

// The league table for seasons from..to (inclusive): one row per club that
// played in them, with pos, matches, won/drawn/lost, goals, gd, pts (after
// deductions), ppg, seasons, first/last season in the range.
export function tableFor(data, from, to) {
  const rec = data.teams.map((t, idx) => ({
    idx,
    team: t.team,
    image: t.image,
    lastSeasonOverall: t.lastSeason,
    matches: 0,
    won: 0,
    drawn: 0,
    lost: 0,
    goalsFor: 0,
    goalsAgainst: 0,
    seasonSet: new Set(),
  }));
  for (const m of data.matches) {
    if (m.season < from || m.season > to) continue;
    for (const [i, gf, ga] of [
      [m.h, m.hs, m.as],
      [m.a, m.as, m.hs],
    ]) {
      const r = rec[i];
      r.matches++;
      r[gf > ga ? "won" : gf < ga ? "lost" : "drawn"]++;
      r.goalsFor += gf;
      r.goalsAgainst += ga;
      r.seasonSet.add(m.season);
    }
  }
  return rec
    .filter((r) => r.matches > 0)
    .map((r) => {
      const deducted = deductionsIn(r.team, from, to);
      const pts = 3 * r.won + r.drawn - deducted;
      const seasons = [...r.seasonSet];
      return {
        ...r,
        deducted,
        pts,
        gd: r.goalsFor - r.goalsAgainst,
        ppg: pts / r.matches,
        seasons: seasons.length,
        firstSeason: Math.min(...seasons),
        lastSeason: Math.max(...seasons),
      };
    })
    .sort(rankOrder)
    .map((r, i) => ({ ...r, pos: i + 1 }));
}

// Match-day-by-match-day running totals for seasons from..to: one step per
// date with at least one Premier League match, so every result is its own
// step (a club can play twice in a week) and summers don't flatten the chart.
// Deductions land on the last match day of their season.
// Returns { days: [{ date, season }], stats: per-team cumulative Int32Arrays
// { P, W, D, L, GF, GA, DED }, positionAt (lazy) }.
export function raceFor(data, from, to) {
  const days = [];
  const dayOf = new Map();
  const lastDayOfSeason = new Map();
  const inRange = data.matches.filter((m) => m.season >= from && m.season <= to);
  for (const m of inRange) {
    if (!dayOf.has(m.date)) {
      dayOf.set(m.date, days.length);
      days.push({ date: m.date, season: m.season });
    }
    lastDayOfSeason.set(m.season, dayOf.get(m.date));
  }
  const n = days.length;
  const blank = () => ({ P: new Int32Array(n), W: new Int32Array(n), D: new Int32Array(n), L: new Int32Array(n), GF: new Int32Array(n), GA: new Int32Array(n), DED: new Int32Array(n) });
  const stats = data.teams.map(blank);
  for (const m of inRange) {
    const w = dayOf.get(m.date);
    for (const [i, gf, ga] of [
      [m.h, m.hs, m.as],
      [m.a, m.as, m.hs],
    ]) {
      const s = stats[i];
      s.P[w]++;
      if (gf > ga) s.W[w]++;
      else if (gf < ga) s.L[w]++;
      else s.D[w]++;
      s.GF[w] += gf;
      s.GA[w] += ga;
    }
  }
  data.teams.forEach((t, i) => {
    for (const d of POINT_DEDUCTIONS) {
      if (d.team === t.team && lastDayOfSeason.has(d.season)) stats[i].DED[lastDayOfSeason.get(d.season)] += d.points;
    }
  });
  for (const s of stats) for (const k of Object.keys(s)) for (let w = 1; w < n; w++) s[k][w] += s[k][w - 1];

  let positions = null; // [team][day] -> 1-based table position, or 0 before a club's first match
  const positionAt = () => {
    if (positions) return positions;
    const pos = data.teams.map(() => new Int16Array(n));
    for (let w = 0; w < n; w++) {
      const rows = [];
      for (let i = 0; i < stats.length; i++) {
        const s = stats[i];
        if (s.P[w] > 0) rows.push({ i, team: data.teams[i].team, pts: 3 * s.W[w] + s.D[w] - s.DED[w], gd: s.GF[w] - s.GA[w], goalsFor: s.GF[w] });
      }
      rows.sort(rankOrder);
      for (let rank = 0; rank < rows.length; rank++) pos[rows[rank].i][w] = rank + 1;
    }
    positions = pos;
    return positions;
  };
  return { days, stats, positionAt, clubsInRange: stats.filter((s) => n && s.P[n - 1] > 0).length };
}

// What the chart can plot. value(s, w, race, i) returns a number, or null
// where it isn't defined yet (before a club's first match).
export const METRICS = [
  { key: "pos", label: "Position", invert: true, format: (v) => `${v}`, value: (s, w, race, i) => race.positionAt()[i][w] || null },
  { key: "pts", label: "Points", format: (v) => v.toLocaleString(), value: (s, w) => 3 * s.W[w] + s.D[w] - s.DED[w] },
  { key: "matches", label: "Played", format: (v) => v.toLocaleString(), value: (s, w) => s.P[w] },
  { key: "won", label: "Won", format: (v) => v.toLocaleString(), value: (s, w) => s.W[w] },
  { key: "drawn", label: "Drawn", format: (v) => v.toLocaleString(), value: (s, w) => s.D[w] },
  { key: "lost", label: "Lost", format: (v) => v.toLocaleString(), value: (s, w) => s.L[w] },
  { key: "gf", label: "Goals for", format: (v) => v.toLocaleString(), value: (s, w) => s.GF[w] },
  { key: "ga", label: "Goals against", format: (v) => v.toLocaleString(), value: (s, w) => s.GA[w] },
  { key: "gd", label: "Goal difference", format: (v) => `${v > 0 ? "+" : ""}${v.toLocaleString()}`, value: (s, w) => s.GF[w] - s.GA[w] },
  { key: "ppg", label: "Points per game", format: (v) => v.toFixed(2), value: (s, w) => (s.P[w] ? (3 * s.W[w] + s.D[w] - s.DED[w]) / s.P[w] : null) },
  { key: "winPct", label: "Win %", format: (v) => `${v.toFixed(1)}%`, value: (s, w) => (s.P[w] ? (100 * s.W[w]) / s.P[w] : null) },
];
